import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Table,
  Tooltip,
  Modal,
  Checkbox,
  Spin,
} from "antd";
import { v4 as uuid } from "uuid";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import uniqueId from "lodash/uniqueId";
import { useDispatch, useSelector } from "react-redux";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { searchIconForInput } from "../../lib/icons";
import ModalWindow from "../ModalWindow/ModalWindow";
import {
  fetchAgreementAmountLeft,
  cleanAgreementAmountLeft,
  transferBalance,
} from "../../store/slices/payments";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import { ReactComponent as RebaseIcon } from "../../img/rebase-icon.svg";
import { hasAccess } from "../RolesModule/hasAccess";
import {
  ACCOUNTS_REG_PAYMENT,
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
  MENU_ACCOUNT_PAYMENTS_WRITE,
  MENU_PAYMENTS_BALANCE_TRANSFER,
} from "../../lib/const/permissions";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import formatNumber from "../../lib/utils/formatNumber";
import SpanNumberFormat from "../common/SpanNumberFormat";
import { ReactComponent as PrepaidBillIcon } from "../../img/payment_icons/prepaid-bill.svg";
import { ReactComponent as PostpaidBillIcon } from "../../img/payment_icons/postpaid-bill.svg";
import { ReactComponent as BillFactureIcon } from "../../img/payment_icons/bill-facture.svg";
import { ReactComponent as InvoiceIcon } from "../../img/payment_icons/invoice.svg";
import { ReactComponent as ServicesIcon } from "../../img/sidemenu-icons/services-icon.svg";
import InputNumberFormat from "../common/InputNumberFormat";
import { round } from "../../lib/utils";
import { limitText } from "../../lib/utils/limitText";
import { ReactComponent as LoupeIcon } from "../../img/loupe-icon.svg";
import { AgreementsSearchModal } from "../AgreementsSearch";
import { agreementsSearchActions } from "../../store/slices/agreementsSearch/agreementsSearch";
import { PaymentRegisterModal } from "./PaymentRegisterModal/PaymentRegisterModal";
import "./AccountPayments.less";

const dateFormat = displayFormat;
const { RangePicker } = DatePicker;

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const buttonLayout = {
  wrapperCol: { span: 24 },
};

export default function AccountPayments(props) {
  const { agreements, paymentsSearchParams, resetSearchPayments, fetchPayments, form } = props;

  const { paymentStatuses, paymentStatusesLoading, paymentSourceTypes } = useSelector(
    (state) => state.rtkDictionaries
  );
  const {
    paymentsAccount,
    paymentsAccountLoading,
    agreementAmountLeft,
    agreementAmountLeftLoading,
    transferBalanceLoading,
  } = useSelector((state) => state.payments);

  const [filtersCollapseState, setFiltersCollapsState] = useState("unfolded");
  const [visibleRegisterPaymentModal, setVisibleRegisterPaymentModal] = useState(false);
  const [visibleTransferBalanceModal, setVisibleTransferBalanceModal] = useState(false);
  const [object, setObject] = useState("");
  const [agreementForRebase, setAgreementForRebase] = useState();
  const [filteredAgreementId, setFilteredAgreementId] = useState("");
  const [visibleClientSearchModal, setVisibleClientSearchModal] = useState(false);
  const [transferBalanceForm] = Form.useForm();
  const [activeIds, setActiveIds] = useState([]);
  const [filterPaymentIdsForm] = Form.useForm();

  const { Panel } = Collapse;
  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const history = useHistory();

  const getTableHeight = () => (filtersCollapseState === "unfolded" ? `${51}vh` : `${64}vh`);

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapsState(newState);
  };

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const handleShowModal = (objId) => {
    setObject(objId);
  };

  const handleHideModal = () => {
    setObject("");
  };

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (checkIsLoaded(paymentsAccountLoading)) {
      const data =
        paymentsAccount &&
        paymentsAccount
          .filter(({ agreement }) => {
            if (filteredAgreementId) return agreement.id === filteredAgreementId;
            return true;
          })
          .filter(({ id }) => {
            if (activeIds.length === 0) {
              return true;
            }
            return activeIds.some((activeId) => String(id).startsWith(String(activeId)));
          })
          .map((payment) => ({
            key: uuid(),
            id: payment.id,
            agreementId: payment?.agreement.id,
            paymentDate: payment.paymentDate ? toDisplayFormat(payment.paymentDate) : "",
            confirmDate: payment.confirmDate ? toDisplayFormat(payment.confirmDate) : "",
            amount: round(payment.amount),
            amountLeft: payment.amountLeft,
            goals: payment?.goals,
            goalsAmount: payment?.goals,
            periodBill: payment?.goals,
            amountBill: payment?.goals,
            amountLeftBill: payment?.goals,
            paymentStatusId: payment?.paymentStatusId,
            parentPaymentId: payment?.parentPaymentId,
            agreementNumber: payment?.agreement?.agreementNumber,
            reserveLeftAmount: payment?.extra?.reserveLeftAmount,
            usedAmount: payment?.extra?.usedAmount,
            availableAmount: payment?.extra?.availableAmount,
            transactionId: payment.transactionId,
            sourceTypeName: paymentSourceTypes.find(({ id }) => id === payment.sourceTypeId)?.name,
            sourceTypeId: payment?.sourceTypeId,
            documentNumber: payment?.info?.documentNumber,
            purpose: payment?.info?.purpose,
          }));
      setTableData(data);
    }
  }, [paymentsAccount, filteredAgreementId, activeIds]);

  const [searchClientType, setSearchClientType] = useState("ORGANIZATION");

  const hasAccessToOrganization = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setSearchClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setSearchClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleSearchClientType = () => {
    if (allowedClientChange) {
      setSearchClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      dispatch(agreementsSearchActions.cleanAccounts());
    }
  };

  // Установка общей суммы остатка
  const availableAmountSum = useMemo(() => {
    const sum = paymentsAccount
      .filter(({ agreement }) =>
        filteredAgreementId ? agreement.id === filteredAgreementId : true
      )
      .reduce((acc, payment) => {
        const availableAmount = Number(payment.extra.availableAmount);
        if (isNaN(availableAmount)) {
          return 0;
        }
        return acc + availableAmount;
      }, 0);
    return sum;
  }, [paymentsAccount, filteredAgreementId]);

  const renderAgreementTitle = () => (
    <Select
      style={{ width: "95%" }}
      placeholder="Договор"
      allowClear
      onChange={setFilteredAgreementId}
      showSearch
      filterOption={(inputValue, { children }) =>
        children.toLowerCase().includes(inputValue.toLowerCase())
      }
    >
      {agreements.map(({ agreementNumber, id }) => (
        <Select.Option key={id} value={id}>
          {agreementNumber}
        </Select.Option>
      ))}
    </Select>
  );

  renderAgreementTitle.toString = () => "Договор";

  const getGoalIcon = (goalTypeId) => {
    const icons = {
      1: [<PrepaidBillIcon />, "Счет на предоплату"],
      2: [<PostpaidBillIcon />, "Счет на постоплату"],
      3: [<BillFactureIcon />, "Счет-фактура"],
      4: [<InvoiceIcon />, "Инвойс"],
      5: [<ServicesIcon key={1} color="#00BBFF" />, "Предложение"],
      6: [<ServicesIcon key={1} color="#0057FF" />, "Продукт"],
    };
    const [icon, title] = icons[goalTypeId];
    return (
      <div style={{ marginRight: "5px", width: "24px", height: "24px" }}>
        <Tooltip title={title}>{icon}</Tooltip>
      </div>
    );
  };

  const renderIdTitle = () => {
    const notValidRgx = /[^0-9,]/;
    let valueTrim = "";
    const onChangeId = ({ target: { value } }) => {
      valueTrim = value.replace(/^\s+/g, "");
      filterPaymentIdsForm.setFieldsValue({ ids: valueTrim });
      if (!notValidRgx.test(valueTrim)) {
        const activeIdes = valueTrim?.split(",").filter(Boolean).map(Number);
        setActiveIds(activeIdes);
      }
    };

    return (
      <Tooltip title="Введите ID платежей через ','">
        <Form form={filterPaymentIdsForm}>
          <Form.Item
            name="ids"
            style={{ marginBottom: 0 }}
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (_, value) => {
                  if (value && notValidRgx.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              aria-autocomplete="none"
              placeholder="ID Платежей"
              allowClear
              className="table-head-filter-search"
              suffix={searchIconForInput()}
              onChange={onChangeId}
            />
          </Form.Item>
        </Form>
      </Tooltip>
    );
  };

  const columnsWidths = {
    id: "85px",
    agreementId: "70px",
    agreementNumber: "85px",
    confirmDate: "85px",
    amount: "85px",
    reserveLeftAmount: "85px",
    usedAmount: "85px",
    availableAmount: "85px",
    paymentDate: "85px",
    documentNumber: "85px",
    purpose: "200px",
    paymentStatusId: "95px",
    sourceTypeName: "93px",
    goals: "200px",
    goalsAmount: "85px",
    periodBill: "85px",
    amountBill: "85px",
    amountLeftBill: "85px",
    actions: "100px",
    buttons: "25px",
  };

  renderIdTitle.toString = () => "ID платежей";

  const getColumns = () => [
    {
      title: renderIdTitle,
      dataIndex: "id",
      key: "id",
      hidden: true,
      width: columnsWidths.id,
      render: (id) => (
        <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{id}</span>
      ),
    },
    {
      title: "ID договора",
      dataIndex: "agreementId",
      key: "agreementId",
      width: columnsWidths.agreementId,
      render: (agreementId) => (
        <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{agreementId}</span>
      ),
      hidden: true,
    },
    {
      title: renderAgreementTitle,
      dataIndex: "agreementNumber",
      key: "agreementNumber",
      width: columnsWidths.agreementNumber,
      render: (agreementNumber) => (
        <div style={{ wordWrap: "break-word", margin: "0 5px" }}>{agreementNumber}</div>
      ),
      hidden: false,
    },
    {
      title: "Дата обработки",
      dataIndex: "confirmDate",
      key: "confirmDate",
      width: columnsWidths.confirmDate,
      hidden: false,
      sorter: (a, b) =>
        moment(a.confirmDate, "DD.MM.YYYY").unix() - moment(b.confirmDate, "DD.MM.YYYY").unix(),
      render: (record) => <span>{record}</span>,
    },
    {
      title: "Сумма платежа",
      dataIndex: "amount",
      key: "amount",
      width: columnsWidths.amount,
      hidden: false,
      render: (value, { hasAdjusment }) => (
        <span style={{ color: hasAdjusment ? "#0af" : "" }}>
          {SpanNumberFormat({ amount: value })}
        </span>
      ),
    },
    {
      title: "Резерв",
      dataIndex: "reserveLeftAmount",
      key: "reserveLeftAmount",
      width: columnsWidths.reserveLeftAmount,
      hidden: true,
      render: (value) => SpanNumberFormat({ amount: value }),
    },
    {
      title: "Использовано",
      dataIndex: "usedAmount",
      key: "usedAmount",
      width: columnsWidths.usedAmount,
      hidden: true,
      render: (value) => SpanNumberFormat({ amount: value }),
    },
    {
      title: "Остаток",
      dataIndex: "availableAmount",
      key: "availableAmount",
      width: columnsWidths.availableAmount,
      hidden: false,
      render: (value) => SpanNumberFormat({ amount: value }),
    },
    {
      title: "Дата платежа",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: columnsWidths.paymentDate,
      hidden: false,
      defaultSortOrder: "descend",
      sorter: (a, b) =>
        moment(a.paymentDate, "DD.MM.YYYY").unix() - moment(b.paymentDate, "DD.MM.YYYY").unix(),
      render: (record) => <span>{record}</span>,
    },
    {
      title: "Номер документа",
      dataIndex: "documentNumber",
      key: "documentNumber",
      hidden: true,
      width: columnsWidths.documentNumber,
    },
    {
      title: "Назначение",
      dataIndex: "purpose",
      key: "purpose",
      hidden: true,
      width: columnsWidths.purpose,
      render: (purpose) => <span title={purpose}>{limitText(purpose, 80)}</span>,
    },
    {
      title: "Статус",
      dataIndex: "paymentStatusId",
      key: "paymentStatusId",
      width: columnsWidths.paymentStatusId,
      hidden: false,
      render: (record) => (
        <span>{paymentStatuses.find(({ id }) => String(id) === String(record))?.name || ""}</span>
      ),
    },
    {
      title: "Источник платежа",
      dataIndex: "sourceTypeName",
      key: "sourceTypeName",
      width: columnsWidths.sourceTypeName,
      hidden: true,
    },
    {
      title: "Цель платежа",
      dataIndex: "goals",
      key: "goals",
      width: columnsWidths.goals,
      className: "account-payments-table__goals",
      hidden: false,
      render: (goals) =>
        goals.map((item) => (
          <div className="account-payments__inner-cell" key={item.id}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {getGoalIcon(item.goalTypeId)}
              <div>
                {/*	Для Типа цели goalTypeId = 3 (счет-фактура) вместо goalId выводим goals: invoices: extNum 
                Если в массиве invoices несколько элементов, то берем из первого. */}
                <div style={{ wordWrap: "break-word" }}>
                  {item.goalTypeId === 3 ? item.invoices?.[0]?.extNum : item.goalId}
                </div>
                {item.goalTypeId === 4 && (
                  <div style={{ wordWrap: "break-word" }}>{item.invoices?.[0]?.extNum}</div>
                )}
              </div>
            </div>
          </div>
        )),
    },
    {
      title: "Сумма цели",
      dataIndex: "goalsAmount",
      key: "goalsAmount",
      width: columnsWidths.goalsAmount,
      hidden: false,
      render: (goals) => (
        <>
          {goals.map(({ id, amount }) => (
            <div
              className="account-payments__inner-cell account-payments__inner-cell_date"
              key={id}
            >
              <span>{amount ? SpanNumberFormat({ amount }) : "-"}</span>
            </div>
          ))}
        </>
      ),
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
      render: (_, record) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleShowModal(record);
          }}
          className="button-icon account-payments__btn"
          style={{ outline: "none", paddingTop: "4px" }}
          icon={<DotsIcon />}
        />
      ),
    },
  ];

  const [columns, setColumns] = useState(getColumns());

  useEffect(() => {
    // Установка columns через getColumns и сеттер, для корректной работы фильтра договоров и отображения статусов
    if (!isEmpty(agreements) && !isEmpty(paymentStatuses)) {
      setColumns((prev) =>
        getColumns().map((column, i) => ({ ...column, hidden: prev[i].hidden }))
      );
    }
  }, [agreements, paymentStatuses]);

  const openClientSearchModal = () => {
    setVisibleClientSearchModal(true);
  };

  const balanceTransferModal = () => {
    const closeBalanceTransferModal = () => {
      setVisibleTransferBalanceModal(false);
      transferBalanceForm.resetFields();
      dispatch(cleanAgreementAmountLeft());
    };

    const onSubmit = async (values) => {
      const targetAgreementId = transferBalanceForm.getFieldValue("agreementToId"); // Получение через getFieldValue, т.к. поля в форме нет.
      const transferData = {
        agreementId: values.agreementFrom,
        targetAgreementId,
        transferDate: toHttpFormat(moment()),
        transferAmount: values.transferAllsum ? agreementAmountLeft : Number(values.transferAmount),
        dsc: values.dsc,
      };

      if (targetAgreementId === values.agreementFrom) {
        message.destroy();
        return message.error("Перенос платежа на тот же самый договор запрещен");
      }

      dispatch(transferBalance(transferData)).then(() => {
        setVisibleTransferBalanceModal(false);
        transferBalanceForm.resetFields();
        setTimeout(() => {
          form.submit(); // поиск платежей
        }, 2e3); // пришлось поставить таймаут, т.к. на бэке не успевают балансы обновиться
        Modal.success({ content: "Баланс перенесен" });
      });
    };

    const limitBalanceTransfer = {
      min: 0.01,
      max: agreementAmountLeft,
    };

    const agreementTo = transferBalanceForm.getFieldValue("agreementTo");

    return (
      <ModalWindow
        visible={visibleTransferBalanceModal}
        title="Вы действительно хотите перенести денежные средства?"
        handleSubmit={transferBalanceForm.submit}
        handleCancel={closeBalanceTransferModal}
        handleReset={closeBalanceTransferModal}
        cancelTextButton="Отмена"
        submitTextButton="Далее"
        width="750px"
        disabled={!agreementTo}
        submitTooltip={agreementTo ? "" : "Необходимо выбрать договор для переноса"}
      >
        <Spin spinning={checkIsLoading(transferBalanceLoading)}>
          <Form form={transferBalanceForm} onFinish={onSubmit} labelCol={{ span: 10 }}>
            <Form.Item
              name="agreementFrom"
              label="Перенести с договора"
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Select
                placeholder="Договор не выбран"
                allowClear
                showSearch
                filterOption={(inputValue, { children }) =>
                  children.toLowerCase().includes(inputValue.toLowerCase())
                }
                onSelect={(id) => {
                  dispatch(fetchAgreementAmountLeft(id));
                  transferBalanceForm.resetFields(["transferAmount"]);
                }}
                onClear={() => {
                  dispatch(cleanAgreementAmountLeft());
                  transferBalanceForm.resetFields(["transferAmount"]);
                }}
              >
                {agreements.map(({ agreementNumber, id }) => (
                  <Select.Option key={id} value={id}>
                    {agreementNumber}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {/* <Button
              style={{ position: "absolute", width: "275px" }}
              onClick={openClientSearchModal}
            >
              {transferBalanceForm.getFieldValue("agreementTo")
                ? "Выбрать другой договор"
                : "Выбрать договор для переноса баланса"}
            </Button> */}
            <Form.Item
              labelCol={{ span: 10 }}
              name="agreementTo"
              className="account-payments__balanceRebaseAgreement"
              label={
                <div>
                  Клиент
                  <Tooltip title="Открыть расширенный поиск">
                    <LoupeIcon onClick={openClientSearchModal} />
                  </Tooltip>
                </div>
              }
            >
              <Tooltip title={transferBalanceForm.getFieldValue("agreementTo")} placement="topLeft">
                <div>
                  <Input
                    placeholder="Договор не выбран"
                    disabled
                    value={limitText(transferBalanceForm.getFieldValue("agreementTo"), 50)}
                  />
                </div>
              </Tooltip>
            </Form.Item>
            <Form.Item name="transferAllsum" label="Перенос всей суммы" valuePropName="checked">
              <Checkbox
                onChange={() => {
                  transferBalanceForm.resetFields(["transferAmount"]);
                }}
              />
            </Form.Item>

            <div style={{ position: "absolute", left: "495px" }} onClick={openClientSearchModal}>
              <div style={{ display: "flex" }} className="ant-form-item-label">
                <label>Доступно</label>
                <SpanNumberFormat
                  amount={agreementAmountLeft}
                  loading={checkIsLoading(agreementAmountLeftLoading)}
                  style={{ marginTop: "6px" }}
                />
              </div>
            </div>
            <Form.Item dependencies={["transferAllsum", "agreementFrom"]} style={{ margin: 0 }}>
              {({ getFieldValue }) => {
                const transferAllsum = getFieldValue("transferAllsum");
                const agreementFrom = getFieldValue("agreementFrom");
                return (
                  <Form.Item
                    name="transferAmount"
                    label="Сумма переноса"
                    rules={[
                      {
                        required: !transferAllsum,
                        message: "Поле обязательно для заполнения",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }
                          if (
                            Number(value) < limitBalanceTransfer.min ||
                            Number(value) > limitBalanceTransfer.max
                          ) {
                            return Promise.reject();
                          }
                          return Promise.resolve();
                        },
                        message: "Недостаточно средств для переноса",
                      },
                    ]}
                    labelCol={{ span: 10 }}
                    dependencies={["transferAllsum", "agreementFrom"]}
                  >
                    <InputNumberFormat
                      style={{ width: "150px" }}
                      placeholder={
                        agreementFrom
                          ? `${formatNumber(limitBalanceTransfer.min)} - ${formatNumber(
                              limitBalanceTransfer.max
                            )}`
                          : ""
                      }
                      allowNegative={false}
                      allowClear={!transferAllsum}
                      disabled={transferAllsum || !agreementFrom}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              name="dsc"
              label="Основание действия"
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Input.TextArea />
            </Form.Item>
          </Form>
        </Spin>
      </ModalWindow>
    );
  };

  const onSubmitAgreementSearch = (v, [selectedAgreement]) => {
    const agreementTo = `${selectedAgreement.agreementNumber} / ${selectedAgreement.fullName}`;
    transferBalanceForm.setFieldsValue({
      agreementTo,
      agreementToId: selectedAgreement.agreementId,
    });
    setVisibleClientSearchModal(false);
  };

  const onFinish = (values) => {
    if (values) {
      values.unpaged = true;
      values.paymentDateFrom =
        values.datePay && values.datePay[0] ? toHttpFormatStartOfDay(values.datePay[0]) : "";
      values.paymentDateTo =
        values.datePay && values.datePay[1] ? toHttpFormatEndOfDay(values.datePay[1]) : "";

      values.confirmDateFrom =
        values.dateConfirm && values.dateConfirm[0]
          ? toHttpFormatStartOfDay(values.dateConfirm[0])
          : "";
      values.confirmDateTo =
        values.dateConfirm && values.dateConfirm[1]
          ? toHttpFormatEndOfDay(values.dateConfirm[1])
          : "";

      values.datePay = "";
      values.dateConfirm = "";
    }
    fetchPayments(values);
  };

  return (
    <>
      <Form
        form={form}
        labelAlign="left"
        initialValues={paymentsSearchParams}
        {...layout}
        onFinish={onFinish}
      >
        <div className="payments-filters">
          <Row justify="space-between">
            <Col>
              <h1>Платежи</h1>
            </Col>
            <Col>
              {hasAccess(ACCOUNTS_REG_PAYMENT) && (
                <Button type="primary" onClick={() => setVisibleRegisterPaymentModal(true)}>
                  Зарегистрировать платеж
                </Button>
              )}
            </Col>
          </Row>
          <Collapse
            defaultActiveKey="1"
            onChange={toggleFiltersCollapseState}
            expandIconPosition="right"
            style={{ marginBottom: "20px" }}
            className="filter-collapse account-detalization__filter"
          >
            <Panel header={getPanelHeader()} key="1">
              <div style={{ marginTop: "10px" }}>
                <Row>
                  <Col span={11}>
                    <Form.Item label="Дата обработки" name="dateConfirm">
                      <RangePicker style={{ width: "100%" }} format={dateFormat} />
                    </Form.Item>
                  </Col>
                  <Col span={11} offset={1}>
                    <Form.Item label="Сумма платежа" name="amount">
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={11}>
                    <Form.Item label="Дата платежа" name="datePay">
                      <RangePicker style={{ width: "100%" }} format={dateFormat} />
                    </Form.Item>
                  </Col>
                  <Col span={11} offset={1}>
                    <Form.Item label="Статус платежа" name="includePaymentStatusIds">
                      <Select mode="multiple" placeholder="Выберите статус">
                        {paymentStatuses.map(({ id, name }) => (
                          <Select.Option key={uniqueId()} value={id}>
                            {name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={11}>
                    <Form.Item
                      name="paymentIds"
                      label="ID платежей"
                      labelAlign="left"
                      labelCol={{ span: 8 }}
                      style={{ marginBottom: "10px" }}
                      rules={[
                        {
                          validator: (x, value) => {
                            if (value && /[^0-9,]/.test(value)) {
                              return Promise.reject();
                            }
                            return Promise.resolve();
                          },
                          message: "В качестве разделителя может использоваться только запятая",
                        },
                      ]}
                    >
                      <Input placeholder='Введите ID платежей через ","' />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={11} offset={12}>
                    <Form.Item {...buttonLayout} style={{ textAlign: "right", marginBottom: 0 }}>
                      <Button onClick={resetSearchPayments}>Сброс параметров</Button>
                      <Button
                        loading={checkIsLoading(paymentsAccountLoading)}
                        htmlType="submit"
                        type="primary"
                      >
                        Применить
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Panel>
          </Collapse>
        </div>
      </Form>
      {!isEmpty(columns) && (
        <Row gutter={20}>
          <Col span={11} offset={0}>
            <div style={{ fontWeight: "bold" }}>
              Сумма остатка: {SpanNumberFormat({ amount: availableAmountSum })}
              {hasAccess(MENU_PAYMENTS_BALANCE_TRANSFER) && (
                <Tooltip title="Перенести баланс">
                  <RebaseIcon
                    style={{
                      cursor: "pointer",
                      marginLeft: "10px",
                      position: "relative",
                      top: "2px",
                    }}
                    onClick={() => {
                      setVisibleTransferBalanceModal(true);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </Col>
          <Col span={1} offset={12}>
            <div style={{ marginTop: "7px" }}>
              <ToggleColumns
                tableName="account-payments"
                columns={columns}
                setColumns={setColumns}
              />
            </div>
          </Col>
        </Row>
      )}
      <Table
        className="account-payments-table"
        size="small"
        columns={[...columns].filter((item) => !item.hidden)}
        dataSource={tableData}
        loading={checkIsLoading(
          paymentsAccountLoading,
          paymentStatusesLoading,
          transferBalanceLoading
        )}
        pagination={false}
        scroll={{ x: true, y: getTableHeight() }}
        onRow={(rec) => ({
          onClick: () => {
            history.push(`${pathname}/${rec.id}`);
          },
        })}
      />
      {balanceTransferModal()}
      <HistoryAndPropertyModal
        objectTypeSysname="payment"
        objectId={object?.id}
        handleHideModal={handleHideModal}
        visibleModal={object?.id}
        dateObjectFromForCalendar={object?.chargePeriodFrom}
        dateObjectToForCalendar={object?.chargePeriodTo}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE)}
      />
      <AgreementsSearchModal
        clientType={searchClientType}
        toggleClientType={toggleSearchClientType}
        visible={visibleTransferBalanceModal && visibleClientSearchModal}
        onClose={() => setVisibleClientSearchModal(false)}
        onSubmit={onSubmitAgreementSearch}
        okText="Выбрать договор"
      />
      <PaymentRegisterModal
        visibleRegisterPaymentModal={visibleRegisterPaymentModal}
        setVisibleRegisterPaymentModal={setVisibleRegisterPaymentModal}
        agreementForRebase={agreementForRebase}
        setAgreementForRebase={setAgreementForRebase}
        isAccount
        updatePayments={form.submit}
      />
    </>
  );
}
